import React from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lock, Star, Zap, Users, BookOpen, TrendingUp, Share2, MessageSquare, Clock } from 'lucide-react';
import { toast } from 'sonner';

interface SubscriptionGuardProps {
  feature: string;
  children: React.ReactNode;
  fallbackTitle?: string;
  fallbackDescription?: string;
  showPreview?: boolean;
}

const FEATURE_INFO = {
  boss_mode: {
    title: 'Boss Mode',
    description: 'Advanced task management and sprint tracking',
    icon: <Zap className="w-8 h-8 text-startup-gold" />,
    preview: 'Get AI-powered daily tasks, track your progress, and stay focused on building your startup.'
  },
  community_pods: {
    title: 'Community Pods',
    description: 'Connect with fellow entrepreneurs and get support',
    icon: <Users className="w-8 h-8 text-startup-teal" />,
    preview: 'Join discussions, share updates, and learn from other builders in your pod.'
  },
  playbooks: {
    title: 'Launch Playbooks',
    description: 'Step-by-step guides for startup success',
    icon: <BookOpen className="w-8 h-8 text-startup-blue" />,
    preview: 'Access proven templates and strategies for launching your startup.'
  },
  idea_assessment: {
    title: 'Unlimited Idea Assessments',
    description: 'Validate as many ideas as you want with AI analysis',
    icon: <TrendingUp className="w-8 h-8 text-startup-red" />,
    preview: 'Free users get 1 assessment. Pro users get unlimited validations.'
  },
  social_tracker: {
    title: 'Social Tracker',
    description: 'Track and manage your social media presence',
    icon: <Share2 className="w-8 h-8 text-startup-purple" />,
    preview: 'Monitor your social media performance and schedule posts across multiple platforms.'
  },
  prompt_library: {
    title: 'Prompt Library',
    description: 'Access to a library of AI prompts',
    icon: <MessageSquare className="w-8 h-8 text-startup-orange" />,
    preview: 'Discover and use powerful AI prompts to boost your productivity and creativity.'
  }
};

export const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({
  feature,
  children,
  fallbackTitle,
  fallbackDescription,
  showPreview = true
}) => {
  const { subscriptionInfo, startFreeTrial, createCheckoutSession } = useSubscription();

  const canAccess = subscriptionInfo.canAccessFeature(feature);

  if (canAccess) {
    return <>{children}</>;
  }

  const isExpiredTrial = subscriptionInfo.tier === 'free_trial' && subscriptionInfo.isTrialExpired;
  const isRestrictedFeature = ['roi_tracker', 'founder_journal'].includes(feature);

  const featureInfo = FEATURE_INFO[feature as keyof typeof FEATURE_INFO];
  const title = fallbackTitle || featureInfo?.title || 'Premium Feature';
  const description = fallbackDescription || featureInfo?.description || 'This feature requires a premium subscription';

  const handleStartTrial = async () => {
    const success = await startFreeTrial();
    if (success) {
      // Refresh the page to show updated access
      window.location.reload();
    }
  };

  const handleUpgradeToMonthly = async () => {
    const checkoutUrl = await createCheckoutSession('monthly_pro');
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank');
    }
  };

  const handleUpgradeToYearly = async () => {
    const checkoutUrl = await createCheckoutSession('yearly_pro');
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank');
    }
  };

  // Special handling for ROI Tracker and Founder Journal when trial is expired
  if (isExpiredTrial && isRestrictedFeature) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-startup-blue via-startup-navy to-startup-blue p-4 flex items-center justify-center">
        <Card className="max-w-2xl w-full bg-white/95 backdrop-blur-sm shadow-2xl border-0">
          <CardHeader className="text-center pb-6">
            <div className="mx-auto bg-gradient-to-r from-amber-400 to-amber-500 text-white p-3 rounded-full w-16 h-16 flex items-center justify-center mb-4">
              <Clock className="h-8 w-8" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Trial Period Ended
            </CardTitle>
            <CardDescription className="text-gray-600 mt-2">
              Your free trial has ended. Upgrade to continue accessing {featureInfo?.title || 'this feature'} and all other premium features.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <Button 
                onClick={handleUpgradeToMonthly}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-base"
              >
                <Zap className="mr-2 h-5 w-5" />
                Upgrade to Pro - Monthly
              </Button>
              <Button 
                onClick={handleUpgradeToYearly}
                variant="outline"
                className="w-full border-amber-500 text-amber-600 hover:bg-amber-50 hover:text-amber-700 py-6 text-base"
              >
                <Star className="mr-2 h-5 w-5" />
                Upgrade to Pro - Yearly (Save 20%)
              </Button>
            </div>
            <p className="text-xs text-center text-gray-500">
              Get full access to all premium features with a Pro subscription.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-startup-blue via-startup-navy to-startup-blue p-4 flex items-center justify-center">
      <Card className="max-w-2xl w-full bg-white/95 backdrop-blur-sm shadow-2xl border-0">
        <CardHeader className="text-center pb-6">
          <div className="flex justify-center mb-4">
            <div className="relative">
              {featureInfo?.icon || <Lock className="w-12 h-12 text-startup-navy" />}
              <div className="absolute -top-1 -right-1 bg-startup-gold rounded-full p-1">
                <Star className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-startup-navy mb-2">
            Upgrade to Access {title}
          </CardTitle>
          <CardDescription className="text-lg text-cool-gray">
            {description}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {showPreview && featureInfo?.preview && (
            <div className="bg-gradient-to-r from-startup-teal/10 to-startup-peach/10 p-4 rounded-lg border border-startup-teal/20">
              <p className="text-startup-navy text-center">{featureInfo.preview}</p>
            </div>
          )}

          {/* Show trial option if not already on trial */}
          {subscriptionInfo.tier === 'free' && (
            <div className="bg-gradient-to-r from-startup-gold/10 to-startup-red/10 p-6 rounded-lg border border-startup-gold/30">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-startup-navy">Start Your Free Trial</h3>
                  <p className="text-sm text-cool-gray">Get 7 days of full access to all features</p>
                </div>
                <Badge className="bg-startup-gold text-white">FREE</Badge>
              </div>
              <Button 
                onClick={handleStartTrial}
                className="w-full bg-gradient-to-r from-startup-gold to-startup-red text-white hover:opacity-90 transition-opacity"
              >
                Start Free Trial
              </Button>
            </div>
          )}

          {/* Show trial expired message if trial is over */}
          {subscriptionInfo.tier === 'free_trial' && subscriptionInfo.isTrialExpired && (
            <div className="bg-gradient-to-r from-startup-red/10 to-startup-navy/10 p-4 rounded-lg border border-startup-red/30">
              <p className="text-startup-red text-center font-medium">
                Your free trial has expired. Upgrade to continue using premium features.
              </p>
            </div>
          )}

          {/* Show trial remaining if still active */}
          {subscriptionInfo.tier === 'free_trial' && !subscriptionInfo.isTrialExpired && (
            <div className="bg-gradient-to-r from-startup-teal/10 to-startup-blue/10 p-4 rounded-lg border border-startup-teal/30">
              <p className="text-startup-navy text-center font-medium">
                {subscriptionInfo.remainingTrialDays} days left in your free trial
              </p>
            </div>
          )}

          {/* Pricing options */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-startup-teal/30 rounded-lg p-4 bg-gradient-to-br from-white to-startup-teal/5">
              <div className="text-center mb-4">
                <h4 className="font-bold text-startup-navy">Monthly Pro</h4>
                <div className="text-2xl font-bold text-startup-teal">$5.99<span className="text-sm font-normal">/month</span></div>
              </div>
              <Button 
                onClick={handleUpgradeToMonthly}
                variant="outline"
                className="w-full border-startup-teal text-startup-teal hover:bg-startup-teal hover:text-white"
              >
                Choose Monthly
              </Button>
            </div>

            <div className="border border-startup-gold/30 rounded-lg p-4 bg-gradient-to-br from-white to-startup-gold/5 relative">
              <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-startup-gold text-white">
                Best Value
              </Badge>
              <div className="text-center mb-4 mt-2">
                <h4 className="font-bold text-startup-navy">Yearly Pro</h4>
                <div className="text-2xl font-bold text-startup-gold">$59.99<span className="text-sm font-normal">/year</span></div>
                <p className="text-xs text-cool-gray">Save $11.89</p>
              </div>
              <Button 
                onClick={handleUpgradeToYearly}
                className="w-full bg-gradient-to-r from-startup-gold to-startup-red text-white hover:opacity-90"
              >
                Choose Yearly
              </Button>
            </div>
          </div>

          <div className="text-center pt-4 border-t">
            <p className="text-xs text-cool-gray">
              All plans include unlimited access to Boss Mode, Community Pods, Launch Playbooks, and more.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};